import { useState, useCallback, useRef, useEffect } from "react";
import { observer } from "@legendapp/state/react";
import {
  videoPlayed$,
  videoDuration$,
  scrubberSeekFraction$,
  activeLessonData$,
  updateActiveWord,
  scrubbing$,
} from "../../state/videoState";

function fmtTime(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface ScrubberProps {
  /**
   * Pin the bar flush to the bottom edge of the (relative) video container as a
   * thin YouTube/Instagram-style overlay, instead of rendering as a full-height
   * row in a strip below the video. The grab area extends upward (pt) so the
   * visible line sits at the very bottom; the line itself thickens on hover/drag.
   */
  overlay?: boolean;
}

const Scrubber = observer(function Scrubber({ overlay = false }: ScrubberProps) {
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekValue, setSeekValue] = useState(0);

  const played = videoPlayed$.get();
  const duration = videoDuration$.get();
  const videoActivity = activeLessonData$.get();

  const lessonStartMs = videoActivity?.start ?? 0;
  const lessonEndMs = videoActivity?.end ?? 0;
  const lessonDurationSec = (lessonEndMs - lessonStartMs) / 1000;
  const lessonStartSec = lessonStartMs / 1000;

  const lessonPlayed =
    duration > 0
      ? Math.max(0, Math.min(1, (played * duration - lessonStartSec) / lessonDurationSec))
      : 0;

  const displayValue = isSeeking ? seekValue : lessonPlayed;
  const percent = displayValue * 100;

  const lessonStartMsRef = useRef(lessonStartMs);
  lessonStartMsRef.current = lessonStartMs;
  const lessonDurationMsRef = useRef(lessonEndMs - lessonStartMs);
  lessonDurationMsRef.current = lessonEndMs - lessonStartMs;

  const trackRef = useRef<HTMLDivElement>(null);
  const seekingRef = useRef(false);

  // If the scrubber unmounts mid-drag (lesson switch / route change) pointerup
  // never fires, so clear the global scrubbing flag on unmount — otherwise it
  // stays true and freezes the playing video's active-word tracking.
  useEffect(
    () => () => {
      if (seekingRef.current) scrubbing$.set(false);
    },
    [],
  );

  const fractionFromPointer = useCallback((clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    return Math.max(0, Math.min(0.999999, (clientX - rect.left) / rect.width));
  }, []);

  const scrubTo = useCallback((fraction: number) => {
    setSeekValue(fraction);
    // Drive the transcript LIVE as the user drags (iOS updates the highlighted
    // word in real time while scrubbing, not only on release). The word timings
    // are absolute video ms, so map the lesson-relative fraction back to an
    // absolute time. The actual video seek still happens on pointer-up.
    const scrubMs =
      lessonStartMsRef.current + fraction * lessonDurationMsRef.current;
    updateActiveWord(scrubMs);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      (e.target as HTMLDivElement).setPointerCapture(e.pointerId);
      seekingRef.current = true;
      setIsSeeking(true);
      scrubbing$.set(true);
      scrubTo(fractionFromPointer(e.clientX));
    },
    [fractionFromPointer, scrubTo],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!seekingRef.current) return;
      scrubTo(fractionFromPointer(e.clientX));
    },
    [fractionFromPointer, scrubTo],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!seekingRef.current) return;
      seekingRef.current = false;
      setIsSeeking(false);
      scrubbing$.set(false);
      scrubberSeekFraction$.set(fractionFromPointer(e.clientX));
    },
    [fractionFromPointer],
  );

  // Overlay: a thin YouTube/Instagram-style bar pinned flush to the bottom edge
  // of the (relative) video container. The grab area extends upward via `pt-3`
  // so the line itself sits at the very bottom; `items-end` drops the track to
  // the bottom of the row. z-30 keeps it above the bottom translation panel/
  // action bar (z-20) so the bar stays draggable. In overlay mode the track is
  // edge-to-edge (no horizontal padding) to read as part of the video chrome.
  return (
    <div
      ref={trackRef}
      className={
        overlay
          ? "absolute inset-x-0 bottom-0 z-30 pt-3 group cursor-pointer"
          : "w-full pt-1 pb-3 md:pb-5 group cursor-pointer"
      }
      style={{ touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Live position readout while dragging (iOS shows "m:ss / m:ss" that
          updates as you scrub). Sits just above the bar, centred. */}
      {isSeeking && lessonDurationSec > 0 && (
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 pointer-events-none px-2.5 py-1 rounded-lg bg-black/80 text-white text-sm font-bold tabular-nums shadow-lg whitespace-nowrap">
          {fmtTime(seekValue * lessonDurationSec)} / {fmtTime(lessonDurationSec)}
        </div>
      )}
      <div
        className={
          overlay
            ? "relative w-full h-[5px] flex items-end pointer-events-none"
            : "relative w-full h-10 md:h-6 flex items-center pointer-events-none"
        }
      >
        {/* Track + fill: edge-to-edge (square) when overlaid on the video,
            rounded pill in the standalone strip. `bottom-0` pins them to the
            video's bottom edge in overlay mode; the centered row keeps the old
            standalone look untouched. */}
        <div
          className={`absolute inset-x-0 h-[3px] group-hover:h-[5px] transition-[height] duration-150 ${overlay ? "bottom-0 bg-white/25" : "rounded-full bg-white/20"}`}
        />
        <div
          className={`absolute left-0 h-[3px] group-hover:h-[5px] transition-[height] duration-150 bg-white ${overlay ? "bottom-0" : "rounded-full"}`}
          style={{ width: `${percent}%` }}
        />
        {/* Hover/drag handle. In overlay mode keep it `bottom-0` (no downward
            translate) so the video container's `overflow-hidden` doesn't clip it
            to a half-circle; it rests on the bar like a YouTube handle. */}
        <div
          className={`absolute w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-[opacity] duration-150 -translate-x-1/2 ${overlay ? "bottom-0" : ""}`}
          style={{ left: `${percent}%` }}
        />
      </div>
    </div>
  );
});

export { Scrubber };
